/* eslint-disable @typescript-eslint/camelcase */
import React, { useState, useEffect, useRef } from 'react';
import Helmet from 'react-helmet';
import { getAppEnv } from '../../../config/env';
import DataLoaderError from '../../../server/errors/DataLoaderError';
import BreadCrumb from '../../components/BreadCrumb/BreadCrumb';
import CabModifySearch from '../../components/CabModifySearch/CabModifySearch';
import PageHeader from '../../components/PageHeader/PageHeader';
import { OUTSTATION_ONE_WAY } from '../../constants/tripTypes';
import { EnvironmentVariables } from '../../types';
import {
  OutStationOneWayDataChecker,
  OutStationOneWayPageData
} from './OutStationWayDataChecker';
import { useMediaQuery } from 'react-responsive';
import './OutStationOneWay.css';
import Footer from '../../components/Footer/Footer';
import FaqSection from '../../components/Faq/Faq';
import CompactList from '../../components/CompactList/CompactList';
import '../CityOperatorTemplate/CityOperatorTemplate.css';
// import AdsComponent from '../../components/adsComponent';
import ReadMore from '../../components/ReadMore/ReadMore';
import '../../components/ReadMore/ReadMore.css';
import { ThrowReporter } from 'io-ts/lib/ThrowReporter';
import { PathReporter } from 'io-ts/lib/PathReporter';
import AppContextProvider from '../../AppContext';
import { trackOneWayCabPageEvent } from '../../utils/omnitureTracking';
import RentalCabs from '../../components/RentalCabs/RentalCabs';
import '../../components/TopCabsRoutes/TopCabsRoutes.css';
import { SEO_OS_HOME_PAGE } from '../../constants/templateConstants';
import { yeildToMainThread } from '../../scheduler';
import {
  detectUserActivity,
  generateRequestId,
  isMobile,
  useSearchWidgetHooks
} from '../../utils/commonUtils';
import { SCROLL } from '../../constants/miscellaneousConstants';
import { makeServiceDeskRequest } from '../../cabsAPIs';
import AssistModal from '../../components/AssistModal';
import CallUsModal from '../../components/AssistModal/callUsModal';
import InboundCallBox from '../../components/InboundCall/InboundCallBox';
import TripAssistant from '../TripAssistant';

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;
const ASSISTED_FORM_DATA_LS = 'ASSISTED_FORM_DATA_LS';

type OutStationOneWayDataProp = {
  data: OutStationOneWayPageData;
};

const runtimeTypChecker = (data: OutStationOneWayPageData) => {
  const result = OutStationOneWayDataChecker.decode(data);
  if (result._tag !== 'Right') {
    // console.log(PathReporter.report(result));
    ThrowReporter.report(result);
  }
};

const OutStationOneWayPage = ({ data }: OutStationOneWayDataProp) => {
  // runtimeTypChecker(data);

  const isMobileDevice = useMediaQuery({
    query: '(max-device-width: 768px)'
  });

  const { travelFor, pickUpTime, dropTime } = useSearchWidgetHooks(
    SEO_OS_HOME_PAGE
  );
  const {
    templateData,
    cabTypesAvailableForRent,
    hrPackages,
    assistedFormOpenDelay = 10000,
    showTripAssistantButton = true,
    showTripAssistantButtonForDweb = true,
    showWhatsappWidget = true,
    whatsappWidgetDeeplink = 'https://wa.me/919951106499?text=Request%20ID%20MDRequest_id%20%0AHi,%20I%20am%20looking%20for%20a%20cab%20from%20Delhi%20to%20Agra',
    customerCareNumber = '01244781861',
    assistantWidgetText = 'You can Call Us or Chat via WhatsApp',
    showWhatsAppForAssistedForm = true,
    showCallUsWidget = false,
    showCallUsForAssistedForm = true,
    inboundCallText = 'Talk to agent (24x7)',
    inboundCallNumber = '0124 - 4781861',
    showInboundCallNumberInDesktop = true,
    showCallusModalMweb = false,
    callusModalOpenDelay = 10000,
    showInboundNumberOnAssistedForm = true,
    couponInfo = null,
    showLeaveIntentForm = false
  } = data;

  const [showAssistantIc, setShowAssistantIc] = useState(false);
  const [showWhatsAppIcon, setWhatsAppIcon] = useState(false);
  const [showCallUsIcon, setCallUsIcon] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const scrollPosition = useRef(0);
  const assistedFormLifeCycle = useRef(0); // 0:init,1:scroll down to threshold, 2: scroll up to threshold, and automated modal shown

  const userActionDone = () => {
    return window?.localStorage?.getItem('userActionDone') == 'true';
  };

  const setShowModalWithDelay = async val => {
    if (isMobile() && (showCallusModalMweb || showTripAssistantButton)) {
      await yeildToMainThread(() =>
        requestAnimationFrame(() => setShowModal(val))
      );
    }
  };
  const showModalConsideringScrollEvents = () => {
    if (assistedFormLifeCycle.current != 2 && !userActionDone()) {
      assistedFormLifeCycle.current = 2;
      setShowModalWithDelay(true);
      const eventName =
        showCallusModalMweb && showCallUsForAssistedForm && isMobile()
          ? 'Cab_SEO_Assisted_Call_Us_Form_Shown'
          : 'Cab_SEO_Assisted_Form_Shown';
      trackOneWayCabPageEvent(eventName);
    }
  };

  useEffect(() => {
    //
    const handleScroll = () => {
      scrollPosition.current = window.scrollY || window.pageYOffset;

      const scrollDownThreshold = isMobileDevice ? 500 : 900;
      const scrollUpThreshold = scrollDownThreshold - 100;

      if (
        scrollPosition.current > scrollDownThreshold &&
        assistedFormLifeCycle.current == 0
      ) {
        assistedFormLifeCycle.current = 1;
      } else if (
        scrollPosition.current < scrollUpThreshold &&
        assistedFormLifeCycle.current == 1
      ) {
        showModalConsideringScrollEvents();
      }
    };

    // If tripAssistantOpen is true in the query params then open form
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('tripAssistantOpen') === 'true') {
      showModalConsideringScrollEvents();
    }

    // Attach the scroll event listener
    window.addEventListener(SCROLL, handleScroll);
    if (window?.localStorage) {
      localStorage.removeItem(ASSISTED_FORM_DATA_LS);
    }

    openFormDueToInactivity();
    if (showCallUsWidget && isMobile()) {
      setTimeout(() => {
        trackOneWayCabPageEvent('Cab_SEO_Call_Us_Icon_Shown');
      }, 500);
    }

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener(SCROLL, handleScroll);
    };
  }, []);

  const openFormDueToInactivity = () => {
    const timeDelay = showCallusModalMweb
      ? callusModalOpenDelay
      : assistedFormOpenDelay;
    setTimeout(() => {
      if (window?.localStorage && assistedFormLifeCycle.current != 2) {
        if (
          !userActionDone() &&
          (isMobile()
            ? showTripAssistantButton || showCallusModalMweb
            : showTripAssistantButtonForDweb)
        ) {
          showModalConsideringScrollEvents();
        } else {
          // not show message should not be shown if modal is already shown
          const userAction = userActionDone();
          trackOneWayCabPageEvent(
            `Cab_SEO_Assisted_Form_Not_Shown_userActionDone_${userAction}`
          );
        }
      }
    }, timeDelay);
  };

  useEffect(() => {
    window.addEventListener(SCROLL, listenToScroll);
    detectUserActivity();
    return () => window.removeEventListener(SCROLL, listenToScroll);
  }, []);

  const listenToScroll = () => {
    const heightToShowFrom = 100;
    const winScroll =
      document.body.scrollTop || document.documentElement.scrollTop;
    if (winScroll > heightToShowFrom) {
      if (!(isMobileDevice && showModal)) {
        setShowAssistantIc(true);
        setWhatsAppIcon(true);
        setCallUsIcon(true);
      }
    } else {
      setShowAssistantIc(false);
      setWhatsAppIcon(false);
      setCallUsIcon(false);
    }
  };

  const fabInteraction = () => {
    return (
      <div
        className={` ${(() => {
          if (showTripAssistantButtonForDweb && showTripAssistantButton) {
            return '';
          } else if (showTripAssistantButton) {
            return 'mobileShow';
          } else if (showTripAssistantButtonForDweb) {
            return 'desktopShow';
          }
          return '';
        })()}`}
      >
        <div
          onClick={() => {
            trackOneWayCabPageEvent(
              `${
                showModal
                  ? 'Cab_SEO_Assisted_Form_Closed'
                  : 'Cab_SEO_Assisted_Form_CTA_Click'
              }`
            );
            setShowModalWithDelay(!showModal);
            assistedFormLifeCycle.current = 2; // keeping this 2 as for both the cases Closed and Clicked, Assisted form will be shown
          }}
          className={`fabInteraction ${
            showModal && isMobileDevice
              ? 'showCrossIc'
              : showAssistantIc
              ? 'showIc'
              : ''
          }`}
        >
          <div className={`icContainer ${showModal ? 'showCrossIc' : ''}`} />
          {true && <span>Trip Assistant</span>}
        </div>
      </div>
    );
  };

  const callUsForm = () => {
    return (
      <div
        className={` ${(() => {
          if (showCallusModalMweb) {
            return '';
          }
          return '';
        })()}`}
      >
        <div
          onClick={() => {
            trackOneWayCabPageEvent(
              `${
                showModal
                  ? 'Cab_SEO_Assisted_Call_Us_Form_Closed'
                  : 'Cab_SEO_Assisted_Form_CTA_Click'
              }`
            );
            setShowModalWithDelay(!showModal);
            assistedFormLifeCycle.current = 2; // keeping this 2 as for both the cases Closed and Clicked, Assisted form will be shown
          }}
          className={`${
            showModal && isMobileDevice
              ? 'fabInteraction showCrossIc'
              : showAssistantIc
              ? 'showIc'
              : ''
          }`}
        >
          <div className={`icContainer ${showModal ? 'showCrossIc' : ''}`} />
        </div>
      </div>
    );
  };

  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const toggleOverlay = (isVisible: boolean) => {
    setIsOverlayVisible(isVisible);
  };

  const onWhatsAppIconClick = () => {
    const requestId = generateRequestId();
    const pageName = 'seo_one_way_page';
    makeServiceDeskRequest(requestId, pageName);
    const whatsAppChatLinkWithRequestId = whatsappWidgetDeeplink
      ? whatsappWidgetDeeplink.replace('MDRequest_id', requestId)
      : '';
    if (whatsAppChatLinkWithRequestId)
      window.location.href = whatsAppChatLinkWithRequestId;
  };

  const onCallUsIconClick = () => {
    window.open(`tel:${customerCareNumber}`, '_blank');
  };

  return (
    <AppContextProvider
      omniUrlArgs={`source=${SEO_OS_HOME_PAGE}`}
      trackTemplateEvent={trackOneWayCabPageEvent}
    >
      <div>
        {isMobile() && showWhatsappWidget && (
          <div
            className={`${!showModal ? 'whatsApp' : ''} ${
              showModal ? '' : showWhatsAppIcon ? 'showWhatsAppIcon' : ''
            }`}
            onClick={() => {
              onWhatsAppIconClick();
              trackOneWayCabPageEvent('Cab_SEO_WhatsApp_Assisted_Icon_Clicked');
            }}
          >
            <div className={`${!showModal ? 'whatsaAppIconContainer' : ''}`} />
            {true && !showModal && <span>Chat with us</span>}
          </div>
        )}
      </div>
      <div>
        {isMobile() && showCallUsWidget && customerCareNumber && (
          <div
            className={`${!showModal ? 'callUs' : ''} ${
              showModal ? '' : showCallUsIcon ? 'showCallUsIcon' : ''
            }`}
            onClick={() => {
              onCallUsIconClick();
              trackOneWayCabPageEvent('Cab_SEO_Call_Us_Icon_Clicked');
            }}
          >
            <div className={`${!showModal ? 'callUsIconContainer' : ''}`} />
            {true && !showModal && <span>Call us now</span>}
          </div>
        )}
      </div>
      {isMobile() && showTripAssistantButton ? fabInteraction() : <div></div>}
      {showCallusModalMweb && callUsForm()}
      {(showTripAssistantButtonForDweb || showLeaveIntentForm) &&
      !isMobile() ? (
        <div className="tripAssistant">
          <TripAssistant
            trackEvent={trackOneWayCabPageEvent}
            autoOpenDelay={assistedFormOpenDelay}
            couponData={couponInfo}
            showTripAssistant={showTripAssistantButtonForDweb}
            showLeaveIntentForm={showLeaveIntentForm}
          />{' '}
        </div>
      ) : (
        <div className="tripAssistant"></div>
      )}
      {showModal && isMobile() && showTripAssistantButton && (
        <AssistModal
          sourceCity={''}
          setShowModal={val => setShowModalWithDelay(val)}
          trackTemplateEvent={trackOneWayCabPageEvent}
          showWhatsAppForAssistedForm={showWhatsAppForAssistedForm}
          showCallUsForAssistedForm={showCallUsForAssistedForm}
          customerCareNumber={customerCareNumber}
          assistantWidgetText={assistantWidgetText}
          onCallUsIconClick={onCallUsIconClick}
          onWhatsAppIconClick={onWhatsAppIconClick}
          showInboundNumberOnAssistedForm={showInboundNumberOnAssistedForm}
          inboundCallNumber={inboundCallNumber}
          inboundCallText={inboundCallText}
        />
      )}
      {showModal && isMobile() && showCallusModalMweb && (
        <CallUsModal
          setShowModal={val => setShowModalWithDelay(val)}
          trackTemplateEvent={trackOneWayCabPageEvent}
          showWhatsAppForAssistedForm={showWhatsAppForAssistedForm}
          showCallUsForAssistedForm={showCallUsForAssistedForm}
          customerCareNumber={customerCareNumber}
          assistantWidgetText={assistantWidgetText}
          onCallUsIconClick={onCallUsIconClick}
          onWhatsAppIconClick={onWhatsAppIconClick}
          showModal={showModal}
        />
      )}
      <div className={`cabSeoDtPage`}>
        <Helmet>
          <title>{templateData.metaTitle}</title>
          <meta name="description" content={templateData.metaDescription} />
          <meta name="keywords" content={templateData.metaKeywords} />
          <link rel="canonical" href={templateData.sampleUrl} />
        </Helmet>
        <div className="infoForCrawler">
          <BreadCrumb breadcrumb={templateData.breadcrumb} />
          <PageHeader
            title={templateData.h1Title}
            extraInfo={templateData.extraInfo}
            isMobileDevice={isMobileDevice}
            isExtraInfoVisible={true}
          />
        </div>
        <div className={`domChanger mobileShow makeSticky`}>
          <CabModifySearch
            isOverlayVisible={isOverlayVisible}
            overlayToggle={toggleOverlay}
            pickupTime={pickUpTime}
            defaultTripType={travelFor}
            returnTime={dropTime}
            isMobileDevice={true}
            title={`Outstation Cabs`}
            omniString={`_OS_OW`}
            hrPackages={hrPackages}
          />
        </div>

        <div className={`desktopShow makeSticky`}>
          <CabModifySearch
            pickupTime={pickUpTime}
            defaultTripType={travelFor}
            returnTime={dropTime}
            returnCloseFlagDefault={false}
            isMobileDevice={false}
            title={`Outstation Cabs`}
            omniString={`_OS_OW`}
            hrPackages={hrPackages}
          />
        </div>
        <main className="container">
          <div style={{ display: 'none' }} className={'infoForUser'}>
            <div className="makeFlex">
              <BreadCrumb breadcrumb={templateData.breadcrumb} />
              {showInboundCallNumberInDesktop ? (
                <InboundCallBox
                  inboundCallText={inboundCallText}
                  inboundCallNumber={inboundCallNumber}
                />
              ) : (
                <div></div>
              )}
            </div>
            <PageHeader
              title={templateData.h1Title}
              extraInfo={templateData.extraInfo}
              isMobileDevice={isMobileDevice}
              isExtraInfoVisible={true}
            />
          </div>
          <div className="appendBottom20">
            {/* {
              <AdsComponent
                template={'City'}
                placedAt={'top'}
                isMobileDevice={isMobileDevice}
                desktopAdConfig={{
                  position: templateData.bannerDesktop
                    ? templateData.bannerDesktop
                    : 'none',
                  class: 'desktopShow appendBottom20 adsLayout',
                  style: {}
                }}
                mobileAdConfig={{
                  position: templateData.bannerMobile
                    ? templateData.bannerMobile
                    : 'none',
                  class: 'mobileShow appendBottom20 adsLayout',
                  style: { marginTop: null }
                }}
              />
            } */}
          </div>
          <RentalCabs
            h2={`One Way Cab Prices`}
            cityName={''}
            data={cabTypesAvailableForRent}
          />
          {templateData &&
            templateData?.readMore &&
            templateData?.readMore.length > 0 && (
              <div className="paddingB20 infoForCrawler">
                <ReadMore data={templateData.readMore} />
              </div>
            )}
          <div
            style={{ flexWrap: 'wrap' }}
            className={`cabsSeoWrap__Details makeFlex visibilityAuto`}
          >
            {data.popularCitiesWithRoutes.map((itemM, idx) => (
              <CompactList
                titlePrefix={`Cabs from `}
                titleSuffix={itemM.cityName}
                list={itemM.routeUrlsList}
                type={'bullets'}
                className={'rightMargin'}
              >
                {(item: any, idx, style) => (
                  <li
                    key={`${item.sourceCityName}_${idx}`}
                    className={`${
                      idx ? `paddingT10 paddingB10` : `paddingB10`
                    }`}
                  >
                    {item.url ? (
                      <a
                        className={`linkBoxFont`}
                        onClick={event => {
                          event.preventDefault();
                          trackOneWayCabPageEvent(
                            `Cab_SEO_OS_OW_Route_Link_Click`
                          );
                          window.location = Object(item.url);
                        }}
                        href={item.url}
                      >
                        {`Cabs from ${item.sourceCityName} to ${item.destinationCityName}`}
                      </a>
                    ) : null}
                  </li>
                )}
              </CompactList>
            ))}
          </div>
          {templateData &&
            templateData?.readMore &&
            templateData?.readMore.length > 0 && (
              <div
                style={{ display: 'none' }}
                className="paddingB20 infoForUser"
              >
                <ReadMore data={templateData.readMore} />
              </div>
            )}
          <section className="visibilityAuto">
            <h2>{`FAQs on One Way Cab Rental Service`}</h2>
            <FaqSection
              trackTemplateEvent={trackOneWayCabPageEvent}
              data={templateData.faq}
              pageUrl={templateData.sampleUrl}
            />
          </section>
        </main>
        <>
          <Footer template={data.templateData} />
        </>
        {isOverlayVisible && (
          <div
            onClick={() => {
              toggleOverlay(false);
            }}
            className="searchWidgetModal mobileShow"
          ></div>
        )}
      </div>
    </AppContextProvider>
  );
};

OutStationOneWayPage.fetchData = async (_, match, urlObj, gRPCClient) => {
  const urlString = `${DATA_LOADER_URL}/api/getOutstationOnewayData`;

  let responseData;
  const response = await fetch(urlString);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', urlString);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', urlString);
    throw new DataLoaderError(`Unable to fetch bus list`, responseData.error);
  }

  const pathArray = urlObj.path.split('/');
  const cacheKey =
    'HTML_STATIC_' + pathArray[pathArray.length - 1].toUpperCase();

  // needs to be  in  a separate file or done differently,
  const templateData = {
    templateName: 'static-one-way-cab',
    metaTitle: 'One Way Cab : Book One Way Taxi Services at Lowest Fare',
    metaDescription:
      'Now book one way cab online at best price and make your journey safe and hassle free with MakeMyTrip. Find best car booking deals on SUVs, Sedan and Hatchbacks at lowest fare.',
    metaKeywords:
      'one way cab, one way drop taxi, one way cab booking, one way taxi service, one way taxi, one way taxi booking, one way cab service, outstation cabs one way, taxi booking',
    analytics: `var s=s_gi('mmtprod');
              s.pageName="seo:domestic cabs:OneWay Cab";
              s.channel="seotemplate domestic cabs funnel";
              s.server = window.location.hostname;
              s.prop24="domestic cabs";
              if(typeof(mboxLoadSCPlugin) != "undefined" ){
              mboxLoadSCPlugin(s);
              }
              var s_code=s.t();if(s_code)document.write(s_code);`,
    h1Title: 'One Way Cab',
    extraInfo:
      'Now book one way cab online at best price and make your journey safe and hassle free with MakeMyTrip. Find best car booking deals on SUVs, Sedan and Hatchbacks at lowest fare.',
    bannerMobile: 'top',
    bannerDesktop: 'top',
    breadcrumb: [
      {
        displayString: 'Home',
        redirectionUrl: 'https://www.makemytrip.com/'
      },
      {
        displayString: 'Cabs',
        redirectionUrl: 'https://www.makemytrip.com/cabs/'
      },
      {
        displayString: ' One Way Cab',
        redirectionUrl: 'https://www.makemytrip.com/car-rental/one-way-cab.html'
      }
    ],
    faq: [
      {
        question: `What is the cost of one way car rental service?`,
        answer: `We offer <span style="font-weight:700"> one way cab </span> booking service for different Indian destinations. The cost of the service depends on the travelling distance, type of vehicle, etc. At MakeMyTrip, we assure you of the most affordable online cab rental service.`
      },
      {
        question: `Can I change the journey date?`,
        answer: `You can change the journey anytime before commencing the journey. We offer you the flexibility to schedule and reschedule the journey according to your comfort. `
      },
      {
        question: `Is there any hidden charges?`,
        answer: `Our <span style="font-weight:700">one way taxi service</span> does not include any hidden charges. You pay the exact amount shown at the time of cab booking.`
      }
    ],
    externalJs: '',
    sampleUrl: 'https://www.makemytrip.com/car-rental/one-way-cab.html',
    footerData: responseData.footerData,
    readMore: [
      {
        title: 'RELIABLE AND FLEXIBLE ONE WAY CAB BOOKING SERVICE IN INDIA',
        description: `<div class="makeInline">
  <p class="makeInline">India's public transport is crowded and not suitable for tourists due to lack of comfort. If you want luxury in your journey from one place to another, <span class="bold">online cab booking</span> is the best option. You can book a cab online and schedule your journey according to your time. The cab will pick you up at your doorstep and drop you off at your preferred destination. <br/> At MakeMyTrip, you get the assurance of a professional cab booking service in India. We offer budget-friendly cabs equipped with premium features to render optimum comfort to the guests. Besides the budget-friendly cabs, you can also hire luxurious sedans for your journey. You can book the one way cab online at our portal and schedule your journey according to your convenience.</p>
</div>`,
        shortDescription: `<div class="makeInline">
  <p class="makeInline">India's public transport is crowded and not suitable for tourists due to lack of comfort. If you want luxury in your journey from one place to another, <span class="bold">online cab booking</span> is the best option. You can book a cab online and schedule your journey according to your time...</p>
</div>`
      },
      {
        title: 'Benefits of Our One Way Cab Rental Service',
        description: `<div>
  <p>A one way taxi service is beneficial for you for many reasons. Find the benefits of renting a one way cab online in the following section.</p>
  <div class="listStyle">
 <ul style="list-style-type:none">
   <li class="paddingBottom10"><div class="headline">
        <h2 class="font16 latoBlack blackText">Schedule and Reschedule the Journey: </h2>
        <p>You can book a cab service anytime online without paying anything in advance. Before the journey commences, you can schedule and reschedule the journey date and time according to your preferences</p>
    </div></li>
   <li class="paddingBottom10"><div class="headline">
        <h2 class="font16 latoBlack blackText">Flexible Rates: </h2>
        <p>We offer a one-way car booking service at the most affordable and flexible rate. The rates vary according to the type of vehicle you select. For a group of two to three people, you can choose a hatchback car for the cheapest rate.</p>
    </div></li>
   <li class="paddingBottom10"><div class="headline">
        <h2 class="font16 latoBlack blackText">Save Your Cost: </h2>
        <p>Compared to roundtrip taxi booking, one-way car rental is cheaper. Roundtrip taxi booking includes the additional price for reserving a vehicle for a few days. But, one-way rental does not include such a fee.</p>
    </div></li>
   <li><div class="headline">
        <h2 class="font16 latoBlack blackText">No Expenses for Driver Accommodation: </h2>
        <p>if you book a roundtrip cab, you have to bear the driver accommodation cost. One way cab booking helps you avoid such expenses, as you leave the car after reaching the destination.</p>
    </div></li>
 </ul>
 </div>
</div>`,
        shortDescription: `<div class="makeInline">
  <p class="paddingB7">A one way taxi service is beneficial for you for many reasons. Find the benefits of renting a one way cab online in the following section.</p>
  <div class="listStyle">
 <ul class="makeInline" style="list-style-type:none">
   <li class="makeInline"><div class="headline makeInline">
        <h2 class="font16 latoBlack blackText">Schedule and Reschedule the Journey: </h2>
        <p>You can book a cab service...</p>
    </div></li>
 </ul>
 </div>
</div>`
      },
      {
        title: 'Find Professional Taxi Rental on Different Routes',
        description: `<div class="makeInline">
  <p class="makeInline">We offer a pan-India taxi rental service through our online portal. You can find affordable and luxurious cars to transfer to any destination from your current location. We offer a <span class="bold">one way cab</span> service for various popular tourist routes in India. For example, you can find a taxi booking service for Mumbai to Goa, Delhi to Agra, Delhi to Jaipur, Chandigarh to Shimla, Amritsar to Dalhousie, Bangaluru to Ooty, and many more. </p>
</div>`,
        shortDescription: `<div class="makeInline">
  <p class="makeInline">We offer a pan-India taxi rental service through our online portal. You can find affordable and luxurious cars to transfer to any destination from your current location. We offer a <span class="bold">one way cab</span> service for various popular tourist routes in India...</p>
</div>`
      },
      {
        title: 'Enjoy a Safe and Comfortable Ride',
        description: `<div class="makeInline"><p class="makeInline"><span class="bold">Online cab booking</span> at MakeMyTrip assures you of your security and comfort. We offer cabs maintained in good condition for a safe and comfortable ride. Moreover, the drivers are well-trained and offer their service with a smile on their faces. Enter your pickup point and destination along with the date and time of your journey to book a cab online.</p></div>`,
        shortDescription: `<div class="makeInline">
  <p class="makeInline"><span class="bold">Online cab booking</span> at MakeMyTrip assures you of your security and comfort. We offer cabs maintained in good condition for a safe and comfortable ride. Moreover, the drivers are well-trained and offer their service with a smile on their faces...</p>
</div>`
      }
    ]
  };

  return {
    pushToCache: true, // if this is set then after ssr it will be stored in cache
    cacheKey,
    ...responseData,
    templateData: templateData
  };
};
export default OutStationOneWayPage;
